import React, { useState } from 'react';
import styled from 'styled-components';

import { InputType } from '../../../shared/constants';
import { WizardOption } from '../../../types/shared';

import EnrichedRadiobutton from '../../atoms/enriched-radiobutton/EnrichedRadiobutton';
import FieldWrapper from '../../atoms/field-wrapper/FieldWrapper';
import FormRadiobutton from '../../atoms/form-radio-button/FormRadiobutton';

import { validateForm } from '../../../utils/forms/formValidation';
import { FORM_LABEL, RADIO_ERROR } from '../../../utils/forms/translationKeys';
import { ITranslationItem } from '../../../utils/language';
import languageHelper from '../../../utils/language/language-helper';

import Colors from '../../../styles/colors';
import { LabelLight, Legend, SmallText } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import ErrorMessage from '../error-message/ErrorMessage';

interface Props {
  id: string;
  title: string;
  optionsLabel?: string;
  helperMessage?: string;
  inputType?: string;
  isRequired?: boolean;
  fields: WizardOption[];
  layout?: string;
  onValueChange: (parentTitle: string, newOption: WizardOption) => void;
  currentSelected?: string;
  customEventHandler?: () => void;
}

const WizardFieldset = ({
  id,
  title,
  optionsLabel,
  helperMessage,
  isRequired,
  fields,
  layout,
  currentSelected,
  onValueChange,
  customEventHandler,
  inputType = InputType.ENRICHED_RADIO,
}: Props) => {
  const [errorMessage, setErrorMessage] = useState<ITranslationItem>();
  const { lang } = languageHelper();

  const onValueChangeHandler = (
    selectedItem: WizardOption,
    validity?: ValidityState
  ) => {
    onValueChange(title, selectedItem);

    if (validity) {
      setErrorMessage(validateForm(validity, title, RADIO_ERROR));
    }
    if (customEventHandler) {
      customEventHandler();
    }
  };

  return (
    <StyledFieldset data-testid="form-fieldset">
      {optionsLabel && <StyledLegend>{optionsLabel}</StyledLegend>}
      {optionsLabel && !isRequired ? (
        <StyledLabelLight> {FORM_LABEL.required[lang]}</StyledLabelLight>
      ) : undefined}
      <FieldWrapper layout={layout} fieldsetType={inputType}>
        {fields?.map((item, index) => {
          if (inputType === InputType.ENRICHED_RADIO) {
            return (
              <EnrichedRadiobutton
                key={index}
                id={`${id}-${index}`}
                name={id}
                value={id}
                label={item?.title}
                error={errorMessage && errorMessage[lang]}
                isRequired={isRequired}
                isChecked={
                  currentSelected !== undefined
                    ? item.id === currentSelected
                    : item.value
                }
                onInvalidHandler={() => onValueChangeHandler(item)}
                image={item?.image || item?.visual?.image}
                layout={layout}
                submitOnClick={!!customEventHandler}
              />
            );
          }
          if (inputType === InputType.DEFAULT_RADIO) {
            return (
              <FormRadiobutton
                key={index}
                id={`${id}-${index}`}
                name={id}
                label={item.title}
                error={errorMessage && errorMessage[lang]}
                isRequired={isRequired}
                value={item.title.toLowerCase()}
                onInvalidHandler={(event) => onValueChangeHandler(item, event)}
                tooltip={item.tooltip}
              />
            );
          }
        })}
      </FieldWrapper>
      {helperMessage && <StyledSmallText>{helperMessage}</StyledSmallText>}
      {errorMessage && (
        <StyledErrorMessage>{errorMessage[lang]}</StyledErrorMessage>
      )}
    </StyledFieldset>
  );
};

export default WizardFieldset;

const StyledFieldset = styled.fieldset`
  border: none;
  margin: ${Spacings.M} 0 0;
  padding: 0;
`;

const StyledLegend = styled(Legend)`
  color: Colors.abnMutedGreen400;
  font-size: 14px;
  margin-bottom: ${Spacings.XS};
`;

const StyledSmallText = styled(SmallText)`
  color: ${Colors.abnNeutral400};
  margin-top: ${Spacings.XS};
`;

const StyledErrorMessage = styled(ErrorMessage)`
  width: 100%;
`;

const StyledLabelLight = styled(LabelLight)`
  color: ${({ theme }) => theme.colors.abnNeutral300};
  font-family: ${({ theme }) => theme.fontRegular};
`;
